import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import UncertaintyForm from "./components/uncertaintyForm";
import { UserType } from "../../types/genericTables.types";
import useAuth from "../../context/AuthContext";
import { uncertaintyMap } from "./utils/helpers";
import {
  IFormNewUncertainty,
  UncertaintyType,
} from "@app/types/uncertainty.types";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { useCalibrationUncertainty } from "@services/servicesGenericsTables";
import { CreatePageList } from "@app/components/Layouts/PageList";
import { Loading, MButton } from "@app/components";
import MessageDialog from "@app/components/Login/MessageDialog";
import { errorAlert } from "@app/utils/alerts";

const getDefaultValues = (
  uncertainty?: UncertaintyType,
  user?: UserType
): IFormNewUncertainty => {
  const today = new Date();

  return {
    uncertainty_source_pat_terc:
      uncertainty?.uncertainty_source_pat_terc || undefined,
    euvps: uncertainty?.euvps || undefined,
    eump_k40: uncertainty?.eump_k40 || undefined,
    eumps_h3: uncertainty?.eumps_h3 || undefined,
    euvps_btr: uncertainty?.euvps_btr || undefined,
    mass_uncertainty_tertiary_pattern:
      uncertainty?.mass_uncertainty_tertiary_pattern || undefined,
    eumps: uncertainty?.eumps,
    eumpp: uncertainty?.eumpp,
    date: uncertainty?.date || today,
    analyst: uncertainty?.analyst || user,
    evh3_d: uncertainty?.evh3_d || undefined,
    evh3_c: uncertainty?.evh3_c,
    evh3: uncertainty?.evh3,
    emh3_d: uncertainty?.emh3_d,
    emh3_c: uncertainty?.emh3_c,
    emh3: uncertainty?.emh3,
    internal_comments: uncertainty?.internal_comments,
    id: uncertainty?.id,
    volume_water_1ml_k2_ml: uncertainty?.volume_water_1ml_k2_ml,
    volume_water_1ml_k2_m3: uncertainty?.volume_water_1ml_k2_m3,
    volume_water_1ml_k1_m3_2: uncertainty?.volume_water_1ml_k1_m3_2,
    volume_water_1ml_k1_l_kg: uncertainty?.volume_water_1ml_k1_l_kg,
    volume_water_1ml_k1_g_2: uncertainty?.volume_water_1ml_k1_g_2,
    volume_water_5ml_k2_ml: uncertainty?.volume_water_5ml_k2_ml,
    volume_water_5ml_k2_m3: uncertainty?.volume_water_5ml_k2_m3,
    volume_water_5ml_k1_m3_2: uncertainty?.volume_water_5ml_k1_m3_2,
    volume_water_5ml_k1_l_kg: uncertainty?.volume_water_5ml_k1_l_kg,
    volume_water_5ml_k1_g_2: uncertainty?.volume_water_5ml_k1_g_2,
    volume_water_25ml_k2_ml: uncertainty?.volume_water_25ml_k2_ml,
    volume_water_25ml_k2_m3: uncertainty?.volume_water_25ml_k2_m3,
    volume_water_25ml_k1_m3_2: uncertainty?.volume_water_25ml_k1_m3_2,
    volume_water_25ml_k1_l_kg: uncertainty?.volume_water_25ml_k1_l_kg,
    volume_water_25ml_k1_g_2: uncertainty?.volume_water_25ml_k1_g_2,
    volume_water_500ml_k2_ml: uncertainty?.volume_water_500ml_k2_ml,
    volume_water_500ml_k2_m3: uncertainty?.volume_water_500ml_k2_m3,
    volume_water_500ml_k1_m3_2: uncertainty?.volume_water_500ml_k1_m3_2,
    volume_water_500ml_k1_l_kg: uncertainty?.volume_water_500ml_k1_l_kg,
    volume_water_500ml_k1_g_2: uncertainty?.volume_water_500ml_k1_g_2,
    volume_sea_5ml_k2_ml: uncertainty?.volume_sea_5ml_k2_ml,
    volume_sea_5ml_k2_m3: uncertainty?.volume_sea_5ml_k2_m3,
    volume_sea_5ml_k1_m3_2: uncertainty?.volume_sea_5ml_k1_m3_2,
    volume_sea_5ml_k1_l_kg: uncertainty?.volume_sea_5ml_k1_l_kg,
    mass_solid_5ml_k2_mg: uncertainty?.mass_solid_5ml_k2_mg,
    mass_solid_5ml_k2_kg: uncertainty?.mass_solid_5ml_k2_kg,
    mass_solid_5ml_k1_kg_2: uncertainty?.mass_solid_5ml_k1_kg_2,
    mass_solid_5ml_k1_g_2: uncertainty?.mass_solid_5ml_k1_g_2,
    mass_solid_25ml_k2_kg: uncertainty?.mass_solid_25ml_k2_kg,
    mass_solid_25ml_k1_kg_2: uncertainty?.mass_solid_25ml_k1_kg_2,
    mass_solid_25ml_k1_g_2: uncertainty?.mass_solid_25ml_k1_g_2,
    mass_solid_200g_k2_mg: uncertainty?.mass_solid_200g_k2_mg,
    mass_solid_200g_k1_mg_2: uncertainty?.mass_solid_200g_k1_mg_2,
    mass_solid_200g_k2_kg: uncertainty?.mass_solid_200g_k2_kg,
    mass_solid_200g_k1_g_2: uncertainty?.mass_solid_200g_k1_g_2,
    volume_sea_5ml_k1_g_2: uncertainty?.volume_sea_5ml_k1_g_2,
    mass_solid_200g_k1_kg_2: uncertainty?.mass_solid_200g_k1_kg_2,
    tipo_incertidumbre: uncertainty?.tipo_incertidumbre || undefined,
    mass_sample_us_k2_mg: uncertainty?.mass_sample_us_k2_mg,
    mass_sample_us_k2_g: uncertainty?.mass_sample_us_k2_g,
    mass_sample_u2s_k1_g2: uncertainty?.mass_sample_u2s_k1_g2,
    mass_sample_ub_k2_mg: uncertainty?.mass_sample_ub_k2_mg,
    mass_sample_ub_k2_g: uncertainty?.mass_sample_ub_k2_g,
    mass_sample_u2b_k1_g2: uncertainty?.mass_sample_u2b_k1_g2,
    mass_solid_minor_200g_k2_mg: uncertainty?.mass_solid_minor_200g_k2_mg,
    mass_solid_minor_200g_k2_kg: uncertainty?.mass_solid_minor_200g_k2_kg,
    mass_solid_minor_200g_k1_kg_2: uncertainty?.mass_solid_minor_200g_k1_kg_2,
    mass_solid_minor_200g_k1_g_2: uncertainty?.mass_solid_minor_200g_k1_g_2,
    AA_k2: uncertainty?.AA_k2,
    AA_k1: uncertainty?.AA_k1,
    NI_k2: uncertainty?.NI_k2,
    NI_k1: uncertainty?.NI_k1,
    FE_k2: uncertainty?.FE_k2,
    FE_k1: uncertainty?.FE_k1,
    NI_MG_k2: uncertainty?.NI_MG_k2,
    NI_MG_k1: uncertainty?.NI_MG_k1,
    NI_FE_k2: uncertainty?.NI_FE_k2,
    NI_FE_k1: uncertainty?.NI_FE_k1,
    inc_vol_sample_k1_u2_m3_2: uncertainty?.inc_vol_sample_k1_u2_m3_2,
    inc_vol_sample_k2_2_m3: uncertainty?.inc_vol_sample_k2_2_m3,
    inc_vol_sample_k2_l: uncertainty?.inc_vol_sample_k2_l,
    k2_calibration_source_mass_uncertainty:
      uncertainty?.k2_calibration_source_mass_uncertainty,
    k1U2_g_calibration_source_mass_uncertainty:
      uncertainty?.k1U2_g_calibration_source_mass_uncertainty,
    result_bq_l: uncertainty?.result_bq_l,
    k40_calibration_source_vol_uncertainty:
      uncertainty?.k40_calibration_source_vol_uncertainty,
  };
};

interface EditUncertaintyProps {
  mode: MODES_OPTIONS;
}

const EditUncertainty = ({ mode }: EditUncertaintyProps) => {
  const { idUncertainty } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isView = mode === MODES_OPTIONS.VIEW;

  const [uncertaintyPreload, setUncertaintyPreload] = useState<
    UncertaintyType | undefined
  >();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [dataToSave, setDataToSave] = useState<IFormNewUncertainty | null>(
    null
  );
  const { save, get, loadingSave } = useCalibrationUncertainty();

  const form = useForm<IFormNewUncertainty>({
    mode: "all",
    defaultValues: getDefaultValues(uncertaintyPreload, user),
  });

  useEffect(() => {
    if (idUncertainty) {
      const uncertainty = get(Number(idUncertainty));
      if (!uncertainty) {
        errorAlert("Incertidumbre no encontrada");
        navigate("/admin/incertidumbres");
        return;
      }
      setUncertaintyPreload(uncertainty);
      const defaultValues = getDefaultValues(uncertainty, user);
      form.reset(defaultValues);
    }
  }, [idUncertainty]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (data: IFormNewUncertainty) => {
    const status = await save(uncertaintyMap(data));
    if (!status) {
      navigate(-1);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSave = (data: IFormNewUncertainty) => {
    if (idUncertainty) {
      setDataToSave(data);
      setOpenConfirmModal(true);
    } else {
      submit(data);
    }
  };

  const handleDialogClose = () => setOpenConfirmModal(false);

  const handleDialogConfirm = () => {
    if (dataToSave) {
      submit(dataToSave);
    }
    handleDialogClose();
  };

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSave)}>
          <CreatePageList
            title={
              isView
                ? "Ver incertidumbre"
                : idUncertainty
                ? "Editar incertidumbre"
                : "Nueva incertidumbre"
            }
            footer={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                {!isView && (
                  <MButton
                    variant="contained"
                    color="primary"
                    text="Guardar"
                    type="submit"
                  />
                )}
                <MButton
                  variant="outlined"
                  color="secondary"
                  sx={{ ml: 2 }}
                  text="Cancelar"
                  onClick={handleBack}
                />
              </Box>
            }
          >
            <UncertaintyForm mode={mode} />
          </CreatePageList>
        </form>
      </FormProvider>
      {loadingSave && <Loading />}
      <MessageDialog
        data={{
          component: "Confirm",
          title:
            "OJO: Va a proceder a Editar esta incertidumbre y puede haber cálculos realizados sobre éstos datos",
          message: "¿Deséa continuar?",
          status: "confirm",
          active: openConfirmModal,
          handleConfirm: handleDialogConfirm,
        }}
        handleClose={handleDialogClose}
        handleConfirm={handleDialogConfirm}
      ></MessageDialog>
    </>
  );
};

export default EditUncertainty;
